import React, { createContext, useContext, useMemo, useState } from 'react';
import type { FacetsData } from '../utils/facets/facets-shared';

/**
 * FacetsContext - Pure state container for facet data
 * 
 * This context DOES NOT make any API calls. It only:
 * 1. Receives initial facet data from SSR
 * 2. Stores and provides that data to components
 * 3. Can be updated when new facet data is available (from search results)
 * 
 * All facet fetching happens server-side or is extracted from search results.
 */

interface FacetsContextValue {
  facetsData: FacetsData;
  setFacetsData: (data: FacetsData) => void;
}

const FacetsContext = createContext<FacetsContextValue | undefined>(undefined);

interface FacetsProviderProps {
  children: React.ReactNode;
  initialFacetsData?: FacetsData;
}

export function FacetsProvider({ children, initialFacetsData }: FacetsProviderProps) {
  const [facetsData, setFacetsData] = useState<FacetsData>(initialFacetsData || {});

  const value = useMemo<FacetsContextValue>(
    () => ({
      facetsData,
      setFacetsData,
    }),
    [facetsData]
  );

  return <FacetsContext.Provider value={value}>{children}</FacetsContext.Provider>;
}

export function useFacets() {
  const ctx = useContext(FacetsContext);
  if (!ctx) {
    throw new Error('useFacets must be used within a FacetsProvider');
  }
  return ctx;
}

export function useFacetsForAttribute(attribute: string) {
  const { facetsData } = useFacets();
  return {
    items: facetsData[attribute] || [],
  };
}
